import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUser } from "../context/UserContext";
import { teamInvitations, teamMembers } from "../services/supabase";

const AcceptInvite: React.FC = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState<boolean>(true);
  const [invitation, setInvitation] = useState<any>(null);
  const [accepting, setAccepting] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (!token) {
      setError("Invalid invitation link");
      setLoading(false);
      return;
    }

    const loadInvitation = async () => {
      try {
        const { data, error } = await teamInvitations.getByToken(token);
        if (error) throw error;

        if (!data) {
          setError("Invitation not found");
          return;
        }

        if (data.status !== 'pending') {
          setError("This invitation has already been used");
          return;
        }

        const expiresAt = new Date(data.expires_at);
        if (expiresAt < new Date()) {
          setError("This invitation has expired");
          return;
        }

        setInvitation(data);
      } catch (err) {
        console.error("Failed to load invitation:", err);
        setError("Failed to load invitation details");
      } finally {
        setLoading(false);
      }
    };

    loadInvitation();
  }, [token]);

  const handleAcceptInvitation = async () => {
    if (!user || !invitation || !token) {
      navigate(`/account?invite=${token}`);
      return;
    }

    setAccepting(true);

    try {
      // Add user to team members
      const { error: memberError } = await teamMembers.create({
        team_id: invitation.team_id,
        user_id: user.id,
        role: 'member'
      });

      if (memberError) throw memberError;

      // Mark invitation as accepted
      const { error: acceptError } = await teamInvitations.accept(token);
      if (acceptError) throw acceptError;

      alert(`You've successfully joined ${invitation.teams?.team_name || 'the team'}!`);
      navigate(`/team/${invitation.team_id}`);

    } catch (err: any) {
      console.error("Failed to accept invitation:", err);
      
      // Check if already a member
      if (err.code === '23505') {
        alert("You're already a member of this team!");
        navigate(`/team/${invitation.team_id}`);
      } else {
        setError(err.message || "Failed to accept invitation");
      }
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-white text-center mt-16">
        <p className="text-gray-400">Loading invitation...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-md mx-auto mt-16 text-center">
        <div className="bg-gray-800 p-8 rounded-2xl border border-red-500">
          <h2 className="text-2xl font-bold text-white mb-4">Invalid Invitation</h2>
          <p className="text-gray-300 mb-6">{error}</p>
          <button
            onClick={() => navigate("/")}
            className="bg-[#A461F9] text-white px-6 py-3 rounded-xl hover:bg-[#934ff2] transition"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-md mx-auto mt-16 text-center">
        <div className="bg-gray-800 p-8 rounded-2xl border border-[#A461F9]">
          <h2 className="text-2xl font-bold text-white mb-4">Team Invitation</h2>
          <p className="text-gray-300 mb-2">
            You've been invited to join <strong className="text-[#A461F9]">{invitation?.teams?.team_name || 'a team'}</strong>!
          </p>
          <p className="text-gray-400 text-sm mb-6">
            Create an account or sign in to accept this invitation.
          </p>
          <button
            onClick={() => navigate(`/account?invite=${token}`)}
            className="w-full bg-[#A461F9] text-white px-6 py-3 rounded-xl hover:bg-[#934ff2] transition font-semibold"
          >
            Create Account & Join Team
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto mt-16 text-center">
      <div className="bg-gray-800 p-8 rounded-2xl border border-[#A461F9]">
        <h2 className="text-2xl font-bold text-white mb-4">Accept Invitation</h2>
        <p className="text-gray-300 mb-2">
          You've been invited to join <strong className="text-[#A461F9]">{invitation?.teams?.team_name || 'a team'}</strong>!
        </p>
        <p className="text-gray-400 text-sm mb-6">
          Click below to join the team.
        </p>
        <button
          onClick={handleAcceptInvitation}
          disabled={accepting}
          className="w-full bg-[#A461F9] text-white px-6 py-3 rounded-xl hover:bg-[#934ff2] transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {accepting ? "Joining..." : "Accept & Join Team"}
        </button>
      </div>
    </div>
  );
};

export default AcceptInvite;